import { Component, OnInit, AfterViewInit, AfterViewChecked, HostListener } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PermissionService } from 'src/app/services/permission.service';
import { GridApi, ICellRendererParams, ColDef } from 'ag-grid-community';
import { NgxSpinnerService } from 'ngx-spinner';
import { forkJoin } from 'rxjs';
import { AddressTooltipComponent } from 'src/app/cell-renderer/address-tooltip/address-tooltip.component';
import { DetailPanelComponent } from 'src/app/cell-renderer/detail-panel/detail-panel.component';
import { LoadingGridComponent } from 'src/app/cell-renderer/loading-grid/loading-grid.component';
import { DepositActionsComponent } from 'src/app/cell-renderer/deposit-actions/deposit-actions.component';
import { ExpenseService } from 'src/app/services/expense.service';
import { ClientService } from 'src/app/services/client.service';
import { OrderService } from 'src/app/services/order.service';
import { UtilsService } from 'src/app/services/utils.service';
import { StatusSelectComponent } from 'src/app/cell-renderer/status-select/status-select.component';
import { SharedDepositGroup } from 'src/app/interfaces/expense.interface';

declare const $: any;

@Component({
    selector: 'app-deposits-list',
    templateUrl: './deposits-list.component.html',
    styleUrls: ['./deposits-list.component.scss']
})
export class DepositsListComponent implements OnInit, AfterViewInit, AfterViewChecked {

    private gridApi!: GridApi;
    private sharedGridApi!: GridApi;
    private baseRows: any[] = [];
    private expandedIds = new Set<any>();
    private pendingExpandServiceId: number | null = null;
    private pendingScrollTop: number | null = null;

    columnDefs: ColDef[] = [];
    public defaultColDef = {
        sortable: true,
        minWidth: 110,
        filter: true,
        tooltipComponent: AddressTooltipComponent,
        resizable: true
    };
    public loadingCellRenderer: any = LoadingGridComponent;
    public loadingCellRendererParams = { loadingMessage: 'Espere un momento...' };

    pageSize = 25;
    pageSizeOptions = [25, 50, 100, 200];
    currentPage = 1;
    hasNextPage = false;

    counts = { pending: 0, finalized: 0, total: 0 };

    dataClient: any[] = [];
    dataStatus: any[] = [];
    data_combo: any = [];

    formModel: any = {
        deposit_status: '',
        service_status: '',
        upload_date: '',
        download_date: '',
        folio: ''
    };

    private folioFilterTimeout: any = null;

    dataUser: any = {};
    private datepickersInitialized = false;

    // ── Tabs ─────────────────────────────────────────────────────────────────
    activeListTab: 'unicos' | 'compartidos' = 'unicos';
    sharedGroups: SharedDepositGroup[] = [];
    sharedGroupsLoading = false;
    expandedGroups = new Set<number>();

    // ── Action Sheet (filtros mobile) ─────────────────────────────────────────
    sheetOpen = false;

    constructor(
        private expenseService: ExpenseService,
        private orderService: OrderService,
        private clientService: ClientService,
        private spinner: NgxSpinnerService,
        private utils: UtilsService,
        private route: ActivatedRoute,
        private router: Router,
        private permissionService: PermissionService
    ) {}

    ngOnInit(): void {
        this.dataUser = this.utils.getDataUser();
        const expandParam = this.route.snapshot.queryParamMap.get('expandService');
        if (expandParam) this.pendingExpandServiceId = parseInt(expandParam, 10);
        const returnPage = this.route.snapshot.queryParamMap.get('returnPage');
        if (returnPage) this.currentPage = parseInt(returnPage, 10);
        const scrollTop = this.route.snapshot.queryParamMap.get('scrollTop');
        if (scrollTop) this.pendingScrollTop = parseInt(scrollTop, 10);
        const tabParam = this.route.snapshot.queryParamMap.get('tab');
        if (tabParam === 'compartidos') {
            this.activeListTab = 'compartidos';
            this.loadSharedList();
        }
        this.buildColumnDefs();
        this.initializeData();
    }

    private initializeData(): void {
        forkJoin([
            this.clientService.getInfo(),
            this.orderService.status()
        ]).subscribe({
            next: ([clients, statuses]) => {
                this.dataClient = clients as any[];
                this.dataStatus = Array.isArray(statuses) ? statuses : (statuses as any)?.data || [];
                this.data_combo = this.dataStatus;
                this.buildDatasource();
            },
            error: () => {
                this.dataClient = [];
                this.dataStatus = [];
                this.data_combo = [];
                this.buildDatasource();
            }
        });
    }

    ngAfterViewInit(): void {
        setTimeout(() => this.initializeDatepickers(), 0);
    }

    ngAfterViewChecked(): void {
        if (this.datepickersInitialized) return;
        this.tryInitializeDatepickers();
    }

    private initializeDatepickers(): void {
        if (!this.isJQueryAvailable() || !this.datepickersExist()) return;
        this.setupDatepicker('dep_upload_date', 'upload_date');
        this.setupDatepicker('dep_download_date', 'download_date');
        this.datepickersInitialized = true;
    }

    private tryInitializeDatepickers(): void {
        try {
            if (this.isJQueryAvailable() && this.datepickersExist()) {
                this.initializeDatepickers();
            }
        } catch {
            // Silently handle initialization errors
        }
    }

    private isJQueryAvailable(): boolean {
        return typeof $ !== 'undefined' && $.fn?.datepicker;
    }

    private datepickersExist(): boolean {
        return $("#dep_upload_date").length > 0 || $("#dep_download_date").length > 0;
    }

    private setupDatepicker(elementId: string, modelProperty: string): void {
        const element = $(`#${elementId}`);
        element.datepicker({ 
            dateFormat: 'yy-mm-dd', 
            onSelect: () => element.trigger('change') 
        }).change(() => {
            this.formModel[modelProperty] = element.datepicker('getDate')?.toISOString().split('T')[0] || '';
            this.filter();
        });
    }

    removeDate(id: string): void {
        const field = id === 'dep_upload_date' ? 'upload_date' : 'download_date';
        try {
            if ($.fn?.datepicker) { $("#" + id).datepicker('setDate', null).trigger('change'); return; }
        } catch { }
        this.formModel[field] = '';
        this.filter();
    }

    private buildColumnDefs(): void {
        const folioCol: ColDef = {
            field: 'service.folio',
            headerName: 'Folio',
            comparator: (valueA: any, valueB: any, nodeA: any, nodeB: any, isInverted: boolean) => {
                const aIsDetail = !!nodeA?.data?.__isDetail;
                const bIsDetail = !!nodeB?.data?.__isDetail;
                const aVal = aIsDetail ? (nodeA?.data?.master?.service?.folio ?? '') : (valueA ?? '');
                const bVal = bIsDetail ? (nodeB?.data?.master?.service?.folio ?? '') : (valueB ?? '');
                if (aVal < bVal) return -1;
                if (aVal > bVal) return 1;
                // Mismo folio: el master siempre va antes que su fila de detalle.
                // Cuando isInverted=true (desc) AG Grid negará este resultado, así que devolvemos
                // el opuesto para que el resultado final siga poniendo el master primero.
                if (!aIsDetail && bIsDetail) return isInverted ? 1 : -1;
                if (aIsDetail && !bIsDetail) return isInverted ? -1 : 1;
                return 0;
            },
            cellRendererSelector: (p: ICellRendererParams) => {
                if (p.data?.__isDetail) return { component: DetailPanelComponent, params: { parent: this, fromDepositList: true } } as any;
                if (p.data?.__isGroupHeader) return {
                    component: (params: ICellRendererParams) => {
                        const g = params?.data;
                        const expanded = this.expandedGroups.has(g?.group_id);
                        const chevron = expanded ? 'ti-chevron-up' : 'ti-chevron-down';
                        const code = g?.group_code ?? '';
                        const count = g?.services_count ?? 0;
                        const hasExtra = g?.unit_name || g?.operator_name;
                        const divider = hasExtra ? `<div style="width:1px;height:34px;background:#C8DFF0;margin:0 12px;flex-shrink:0;"></div>` : '';
                        const unitBlock = g?.unit_name ? `<div style="display:flex;flex-direction:column;gap:1px;margin-right:20px;"><span style="font-size:12px;color:#707070;line-height:1.5;">Unidad</span><span style="font-size:12px;color:#545454;font-weight:500;line-height:1.3;">${g.unit_name}</span></div>` : '';
                        const opBlock = g?.operator_name ? `<div style="display:flex;flex-direction:column;gap:1px;"><span style="font-size:12px;color:#707070;line-height:1.5;">Operador</span><span style="font-size:12px;color:#545454;font-weight:500;line-height:1.5;">${g.operator_name}</span></div>` : '';
                        const activeCount = g?.active_services_count ?? 0;
                        const actionBtns = activeCount >= 2
                            ? `<button data-action="registrar-viaticos" style="background:#fff;border:1.5px solid #00A0E1;color:#00A0E1;border-radius:8px;padding:4px 14px;font-size:12px;font-weight:600;cursor:pointer;white-space:nowrap;line-height:1.5;">Registrar viáticos</button>
                               <button data-action="add-deposit" style="background:#fff;border:1.5px solid #00A0E1;color:#00A0E1;border-radius:50%;width:28px;height:28px;font-size:18px;font-weight:600;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;line-height:1;">+</button>`
                            : '';
                        return `<div style="display:flex;align-items:center;justify-content:space-between;width:100%;height:100%;padding:0 16px;box-sizing:border-box;cursor:pointer">
                          <div style="display:flex;align-items:center;">
                            <span style="background:#00A0E1;color:#fff;font-size:12px;font-weight:700;padding:0 12px;border-radius:6px;height:26px;line-height:26px;white-space:nowrap;flex-shrink:0;">${code}</span>
                            <span style="font-size:13px;font-weight:500;color:#555;margin-left:12px;white-space:nowrap;">${count} Servicio${count !== 1 ? 's' : ''} compartiendo viáticos</span>
                            ${divider}${unitBlock}${opBlock}
                          </div>
                          <div style="display:flex;align-items:center;gap:8px;flex-shrink:0;margin-left:12px;">
                            ${actionBtns}
                            <i class="ti ${chevron}" style="font-size:18px;color:#888;flex-shrink:0;"></i>
                          </div>
                        </div>`;
                    }
                } as any;
                return {
                    component: (params: ICellRendererParams) => {
                        const expanded = this.expandedIds.has(params?.data?.service?.id);
                        const icon = expanded ? 'ti-chevron-down' : 'ti-chevron-right';
                        const folio = params?.value ?? '';
                        const createdAt = params?.data?.service?.created_at ?? '';
                        return `<div style="display:flex;align-items:flex-start;gap:6px;line-height:1.2">
                          <span class="ti ${icon}" style="margin-top:2px"></span>
                          <div><div>${folio}</div>${createdAt ? `<div style="font-size:11px;color:#6c757d;">${createdAt}</div>` : ''}</div>
                        </div>`;
                    }
                } as any;
            },
            colSpan: (p: any) => {
                if (!p?.data?.__isDetail && !p?.data?.__isGroupHeader) return 1;
                const l = p.api.getDisplayedLeftColumns?.()?.length ?? 0;
                const c = p.api.getDisplayedCenterColumns?.()?.length ?? 0;
                const r = p.api.getDisplayedRightColumns?.()?.length ?? 0;
                return l + c + r;
            },
            cellStyle: (p: any) => {
                if (p?.data?.__isDetail) return { padding: 0 };
                if (p?.data?.__isGroupHeader) return { padding: 0, backgroundColor: '#e3f2fd', cursor: 'pointer' };
                return { display: 'flex', alignItems: 'center', textAlign: 'left' } as any;
            }
        };

        this.columnDefs = [
            folioCol,
            { field: 'service.client.name', headerName: 'Cliente', cellClass: 'textEllipsis' },
            { field: 'service.unit.unit', headerName: 'Unidad', cellClass: 'textEllipsis' },
            { field: 'date_start', headerName: 'Fecha/Hora', width: 130, cellClass: 'textEllipsis' },
            { field: 'date_end', headerName: 'Fecha y hora d...', width: 130, cellClass: 'textEllipsis',
                cellRenderer: (p: ICellRendererParams) => p?.value?.trim() ? p.value : 'Prog. pendiente',
                cellStyle: (p: any) => !p?.value?.trim() ? { backgroundColor: '#F9E3E3', color: '#A62E2E', fontWeight: 'bold' } : null
            },
            { field: 'destination_address', headerName: 'Destino', cellClass: 'textEllipsis',
                tooltipField: 'destination_address', tooltipComponentParams: { color: '#ececec' }
            },
            {
                field: 'status_id', headerName: "Estado", headerClass:".text-center-header",
                minWidth: 180,
                filter: false,
                sortable: false,
                cellStyle: (params: any) => {
                    const statusColors = this.getStatusColor(params?.data);
                    return { 
                        backgroundColor: statusColors.bg,
                        color: statusColors.color 
                    } as any;
                },
                cellRenderer: StatusSelectComponent,
                cellRendererParams: { parent: this, data_combo: this.data_combo, dataUser: this.dataUser }
            },
            {
                field: 'deposit_label', headerName: 'Depósitos realizados', minWidth: 160,
                headerClass: '.text-center-header',
                cellRenderer: (p: ICellRendererParams) => {
                    const depositsCount = p?.data?.deposits_count ?? 0;
                    if (depositsCount === 0 && this.isLocalTripDestination(p?.data?.destination_address)) {
                        return 'Viaje local';
                    }
                    if (p?.data && this.isFinishedOrDelivered(p.data)) {
                        return depositsCount === 0 ? 'Deposito sin realizar' : 'Finalizado';
                    }
                    return this.getDepositLabel(depositsCount);
                },
                cellStyle: (p: any) => {
                    const depositsCount = p?.data?.deposits_count ?? 0;
                    let label: string;
                    if (depositsCount === 0 && this.isLocalTripDestination(p?.data?.destination_address)) {
                        label = 'Viaje local';
                    } else if (this.isFinishedOrDelivered(p?.data)) {
                        label = depositsCount === 0 ? 'Deposito sin realizar' : 'Finalizado';
                    } else {
                        label = this.getDepositLabel(depositsCount);
                    }
                    return { ...this.getDepositLabelStyle(label), display: 'flex', alignItems: 'center', justifyContent: 'center' } as any;
                }
            },
            {
                field: 'id', headerName: 'Acciones', filter: false, sortable: false,
                headerClass: '.text-center-header',
                cellRenderer: DepositActionsComponent,
                cellRendererParams: { parent: this },
                cellStyle: () => ({ display: 'flex', alignItems: 'center', justifyContent: 'center' })
            }
        ];
    }

    public getRowHeight(params: any): number {
        return params?.data?.__isDetail ? 160 : 48;
    }

    @HostListener('window:resize')
    onWindowResize(): void {
        if (this.gridApi && window.innerWidth >= 992) this.gridApi.sizeColumnsToFit();
        if (this.sharedGridApi && window.innerWidth >= 992) this.sharedGridApi.sizeColumnsToFit();
    }

    onGridReady(params: any): void {
        if (params?.api) this.gridApi = params.api as GridApi;
        try {
            this.gridApi.setGridOption('getRowHeight', this.getRowHeight as any);
            this.gridApi.setGridOption('suppressScrollOnNewData', true as any);
            this.gridApi.setGridOption('pagination', false);
            this.gridApi.setGridOption('suppressPaginationPanel', true as any);
            if (window.innerWidth >= 992) this.gridApi.sizeColumnsToFit();
            if (this.baseRows.length > 0) {
                this.gridApi.setGridOption('rowData', this.buildDisplayRows());
                this.gridApi.resetRowHeights();
                if (window.innerWidth >= 992) setTimeout(() => this.gridApi.sizeColumnsToFit(), 150);
            }
        } catch { }
    }

    private buildDisplayRows(): any[] {
        const out: any[] = [];
        for (const r of this.baseRows) {
            out.push(r);
            // Usar el mismo ID que se usa en el cellRenderer (service.id)
            const depositId = r.service?.id || r.id;
            if (r && this.expandedIds.has(depositId)) out.push({ __isDetail: true, master: r });
        }
        return out;
    }

    private buildDatasource(): void {
        this.spinner.show();
        const payload = { ...this.formModel, page: this.currentPage, pageSize: this.pageSize };
        this.expenseService.getDepositsList(payload).subscribe({
            next: (resp: any) => {
                if (resp?.counts) this.counts = resp.counts;
                else if (Array.isArray(resp)) {
                    this.counts.total = resp.length;
                    this.counts.pending = resp.filter((r: any) => r.deposit_status === 'pending').length;
                    this.counts.finalized = resp.filter((r: any) => r.deposit_status === 'finalized').length;
                }
                this.baseRows = Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []);
                this.hasNextPage = this.baseRows.length === this.pageSize;
                if (this.pendingExpandServiceId !== null) {
                    const target = this.baseRows.find(r => (r.service?.id || r.id) === this.pendingExpandServiceId);
                    if (target) this.expandedIds.add(this.pendingExpandServiceId);
                    this.pendingExpandServiceId = null;
                }
                const display = this.buildDisplayRows();
                if (this.gridApi) {
                    this.gridApi.setGridOption('rowData', display);
                    this.gridApi.resetRowHeights();
                    try { setTimeout(() => this.gridApi.onRowHeightChanged(), 120); } catch { }
                    if (window.innerWidth >= 992) {
                        setTimeout(() => this.gridApi.sizeColumnsToFit(), 150);
                    }
                    if (this.pendingScrollTop !== null) {
                        const scrollVal = this.pendingScrollTop;
                        this.pendingScrollTop = null;
                        setTimeout(() => {
                            window.scrollTo({ top: scrollVal, behavior: 'instant' as ScrollBehavior });
                        }, 300);
                    }
                }
                this.spinner.hide();
            },
            error: () => this.spinner.hide()
        });
    }

    refreshGrid(): void { this.buildDatasource(); }

    switchTab(tab: 'unicos' | 'compartidos'): void {
        this.activeListTab = tab;
        if (tab === 'compartidos' && this.sharedGroups.length === 0) {
            this.loadSharedList();
        }
    }

    loadSharedList(): void {
        this.sharedGroupsLoading = true;
        this.expenseService.getSharedDepositsList(this.formModel).subscribe({
            next: (res: any) => {
                this.sharedGroups = res?.data || [];
                this.sharedGroupsLoading = false;
                // onSharedGridReady populates when grid renders after *ngIf resolves
            },
            error: () => { this.sharedGroupsLoading = false; }
        });
    }

    onSharedGridReady(params: any): void {
        if (params?.api) this.sharedGridApi = params.api as GridApi;
        try {
            this.sharedGridApi.setGridOption('getRowHeight', this.getRowHeight as any);
            this.sharedGridApi.setGridOption('suppressScrollOnNewData', true as any);
            this.sharedGridApi.setGridOption('pagination', false);
            this.sharedGridApi.setGridOption('suppressPaginationPanel', true as any);
            if (window.innerWidth >= 992) this.sharedGridApi.sizeColumnsToFit();
            if (this.sharedGroups.length > 0) {
                this.sharedGridApi.setGridOption('rowData', this.buildSharedDisplayRows());
                this.sharedGridApi.resetRowHeights();
                if (window.innerWidth >= 992) setTimeout(() => this.sharedGridApi.sizeColumnsToFit(), 150);
            }
        } catch { }
    }

    private buildSharedDisplayRows(): any[] {
        const out: any[] = [];
        for (const group of this.sharedGroups) {
            const firstSvc = group.services?.[0];
            const unitName: string = (group as any).unit ?? (group as any).unit_name ?? firstSvc?.service?.unit?.unit ?? firstSvc?.unified_concat ?? '';
            const operatorName: string = (group as any).driver_name ?? firstSvc?.operator_name ?? '';
            out.push({ __isGroupHeader: true, group_id: group.group_id, group_code: group.group_code, services_count: group.services.length, unit_name: unitName, operator_name: operatorName, active_services_count: group.active_services_count ?? 0, first_active_service_id: group.first_active_service_id });
            if (this.expandedGroups.has(group.group_id)) {
                const groupActiveCount = group.active_services_count ?? 0;
                for (const s of group.services) {
                    out.push({ ...s, __groupActiveCount: groupActiveCount });
                    const sid = s.service?.id || s.service_id || s.id;
                    if (this.expandedIds.has(sid)) out.push({ __isDetail: true, master: s });
                }
            }
        }
        return out;
    }

    onSharedCellClicked(event: any): void {
        const row = event?.data;
        if (row?.__isDetail) return;

        if (row?.__isGroupHeader) {
            const clickTarget = (event.event?.target as HTMLElement)?.closest('[data-action]') as HTMLElement | null;
            if (clickTarget) {
                const action = clickTarget.getAttribute('data-action');
                if (action === 'registrar-viaticos') {
                    const sid = row.first_active_service_id;
                    if (sid) this.router.navigate(['/service/expense-report', sid], { queryParams: { from: 'deposits', tab: 'compartidos' } });
                }
                // 'add-deposit': sin funcionalidad por ahora
                return;
            }
            const groupId = row.group_id;
            if (this.expandedGroups.has(groupId)) this.expandedGroups.delete(groupId);
            else this.expandedGroups.add(groupId);
            const display = this.buildSharedDisplayRows();
            if (this.sharedGridApi) {
                this.sharedGridApi.setGridOption('rowData', display);
                this.sharedGridApi.resetRowHeights();
                try { setTimeout(() => this.sharedGridApi.onRowHeightChanged(), 120); } catch { }
            }
            return;
        }

        if (event.colDef.field !== 'service.folio') return;
        const serviceId = row?.service?.id || row?.service_id || row?.id;
        if (this.expandedIds.has(serviceId)) this.expandedIds.delete(serviceId);
        else this.expandedIds.add(serviceId);
        const display = this.buildSharedDisplayRows();
        if (this.sharedGridApi) {
            this.sharedGridApi.setGridOption('rowData', display);
            this.sharedGridApi.resetRowHeights();
            setTimeout(() => {
                const allRows: any[] = [];
                this.sharedGridApi.forEachNode((node: any) => { allRows.push(node); });
                const target = allRows.find(n => n.data && !n.data.__isDetail && !n.data.__isGroupHeader && (n.data.service?.id === serviceId || n.data.id === serviceId));
                if (target) try { this.sharedGridApi.ensureIndexVisible(target.rowIndex, 'middle'); } catch { }
            }, 50);
            try { setTimeout(() => this.sharedGridApi.onRowHeightChanged(), 120); } catch { }
        }
    }

    toggleGroupExpand(groupId: number): void {
        if (this.expandedGroups.has(groupId)) {
            this.expandedGroups.delete(groupId);
        } else {
            this.expandedGroups.add(groupId);
        }
    }

    isGroupExpanded(groupId: number): boolean {
        return this.expandedGroups.has(groupId);
    }

    getScrollTop(): number {
        return window.scrollY ?? 0;
    }

    filter(): void {
        this.currentPage = 1;
        this.buildDatasource();
    }

    onSelectChange(): void { this.filter(); }

    onFolioInput(): void {
        clearTimeout(this.folioFilterTimeout);
        this.folioFilterTimeout = setTimeout(() => this.filter(), 400);
    }

    nextPage(): void { this.changePage(1); }
    prevPage(): void { this.changePage(-1); }
    onPageSizeChanged(val: any): void {
        const newSize = parseInt(val, 10);
        if (!isNaN(newSize)) {
            this.pageSize = newSize;
            this.currentPage = 1;
            this.buildDatasource();
        }
    }

    private changePage(direction: number): void {
        const canNavigate = direction > 0 ? this.hasNextPage : this.currentPage > 1;
        if (canNavigate) {
            this.currentPage += direction;
            this.buildDatasource();
        }
    }

    /**
     * Get status colors for a row
     */
    private getStatusColor(row: any): { color: string; bg: string } {
        if (!row) return { color: '', bg: '' };
        const serviceColor = row?.status_color || '';
        const serviceBg = row?.bgColor || '';
        
        return { color: serviceColor, bg: serviceBg };
    }

    private getDepositLabelStyle(label: string): { backgroundColor: string; color: string; padding: string; fontWeight: string } {
        if (label === 'Finalizado') return { backgroundColor: '#D4EDDA', color: '#155724', padding: '2px 10px', fontWeight: 'bold' };
        if (label === 'Pendiente')  return { backgroundColor: '#FDE8E8', color: '#A62E2E', padding: '2px 10px', fontWeight: 'bold' };
        if (label === 'Deposito sin realizar') return { backgroundColor: '#FFF3CD', color: '#856404', padding: '2px 10px', fontWeight: 'bold' };
        if (label === 'Viaje local') return { backgroundColor: '#E9ECEF', color: '#495057', padding: '2px 10px', fontWeight: 'bold' };
        return { backgroundColor: '#D0EAF8', color: '#0D5C8A', padding: '2px 10px', fontWeight: 'bold' };
    }

    private getDepositLabel(depositsCount: number): string {
        if (depositsCount === 0) return 'Pendiente';
        if (depositsCount === 1) return 'Depósito inicial';
        return `Depósito #${depositsCount}`;
    }

    /**
     * Servicios cuyo destino cae en estos estados no contemplan gastos/depósito.
     * Si ya existe un depósito registrado, esta bandera se ignora y se respeta el dato real.
     */
    private static readonly LOCAL_TRIP_STATES = [
        'estado de mexico', 'ciudad de mexico', 'cdmx',
        'queretaro', 'morelos', 'tlaxcala', 'hidalgo', 'puebla',
    ];

    private isLocalTripDestination(address?: string | null): boolean {
        if (!address) return false;
        const accentMap: Record<string, string> = { 'á': 'a', 'é': 'e', 'í': 'i', 'ó': 'o', 'ú': 'u', 'ñ': 'n', 'ü': 'u' };
        const normalized = address
            .toLowerCase()
            .replace(/[áéíóúñü]/g, (ch) => accentMap[ch]);
        return DepositsListComponent.LOCAL_TRIP_STATES.some(state => normalized.includes(state));
    }

    private isFinishedOrDelivered(row: any): boolean {
        const statusFromList = this.dataStatus.find((s: any) => String(s.id) === String(row?.status_id));
        const statusName = String(statusFromList?.name ?? '').toLowerCase();
        return statusName.includes('finaliz') || statusName.includes('entregad');
    }

    openSheet(): void {
        this.sheetOpen = true;
        setTimeout(() => this.initializeSheetDatepickers(), 0);
    }

    private initializeSheetDatepickers(): void {
        if (!this.isJQueryAvailable()) return;
        this.setupDatepicker('dep_upload_date_sheet', 'upload_date');
        this.setupDatepicker('dep_download_date_sheet', 'download_date');
    }

    closeSheet(): void { this.sheetOpen = false; }

    hasActiveFilters(): boolean {
        return !!(this.formModel.deposit_status || this.formModel.service_status ||
                  this.formModel.upload_date || this.formModel.download_date || this.formModel.folio);
    }

    clearSheetFilters(): void {
        this.formModel = { deposit_status: '', service_status: '', upload_date: '', download_date: '', folio: '' };
        this.closeSheet();
        this.filter();
    }

    applySheetFilters(): void {
        this.closeSheet();
        this.filter();
    }

    canDo(action: string): boolean {
        return this.permissionService.canDo('Control de gastos', action);
    }

    downloadShared(s: any): void {
        const serviceId = s.service?.id || s.service_id;
        if (!serviceId) return;
        this.expenseService.downloadXls(serviceId).subscribe({
            next: (blob: Blob) => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `validacion_gastos_${serviceId}.xlsx`;
                a.click();
                URL.revokeObjectURL(url);
            }
        });
    }

    goViaticosShared(s: any): void {
        const serviceId = s.service?.id || s.service_id;
        if (serviceId) this.router.navigate(['/service/expense-report', serviceId], { queryParams: { from: 'deposits', tab: 'compartidos' } });
    }

    addDepositShared(s: any): void {
        const serviceId = s.service?.id || s.service_id;
        if (serviceId) this.router.navigate(['/service/deposit', serviceId]);
    }

    onCellClicked(event: any): void {
        const row = event?.data;
        if (row?.__isDetail) return;
        if (event.colDef.field !== 'service.folio') return;

        const depositId = row?.service?.id || row?.id;
        if (this.expandedIds.has(depositId)) this.expandedIds.delete(depositId); else this.expandedIds.add(depositId);
        const display = this.buildDisplayRows();
        if (this.gridApi) {
            this.gridApi.setGridOption('rowData', display);
            this.gridApi.resetRowHeights();
            setTimeout(() => {
                const allRows: any[] = [];
                this.gridApi.forEachNode((node: any) => { allRows.push(node); });
                const targetRow = allRows.find((rowModel: any) =>
                    rowModel.data && !rowModel.data.__isDetail && (rowModel.data.service?.id === depositId || rowModel.data.id === depositId)
                );
                if (targetRow) {
                    try { this.gridApi.ensureIndexVisible(targetRow.rowIndex, 'middle'); } catch { }
                }
            }, 50);
            try { setTimeout(() => this.gridApi.onRowHeightChanged(), 120); } catch { }
        }
    }
}
